
// @ts-nocheck


/**
 * GtcProductCardSwatches
 *
 * @description
 * Handles the product card swatches functionality, including hover interactions
 * for changing images, titles, prices, and compare-at prices on product cards.
 */

const POINTER_QUERY = window.matchMedia('(pointer: fine) and (any-hover: hover)');

class GtcProductCardSwatches extends HTMLElement {
    constructor() {
        super();

        this.IS_POINTER_DEVICE = POINTER_QUERY.matches;

        // Constants for better maintainability
        this.CONSTANTS = {
            SPINNER_DELAY: 100,
            ATTRIBUTES: {
                CHANGE_SETTINGS_ON_TOUCH: 'data-change-settings-on-touch',
            },
            SELECTORS: {
                PRODUCT_CARD: 'data-product-card-selector',
                IMAGE: 'data-image-selector',
                TITLE: 'data-title-selector',
                PRICE: 'data-price-selector',
                COMPARE_AT_PRICE: 'data-compare-at-price-selector',
                SWATCH: '.gtc-swatch',
                IMAGE_CONTAINER: '.gtc-swatch-image-container',
                IMAGE_ITEM: '.gtc-swatch-image',
            },
            CLASSES: {
                CURRENT: 'gtc-swatch--current',
                JS_CURRENT: 'gtc-swatch--js-current',
                ACTIVE: 'gtc-swatch-image--active',
                SPINNER: 'gtc-swatch-image-container--spinner',
                IMAGE_CONTAINER: 'gtc-swatch-image-container',
                IMAGE_ITEM: 'gtc-swatch-image',
                BUTTON: 'gtc-swatches__button',
                HIDE: 'hide',
            }
        };

        this.settings = {
            changeImageOnHover: false,
            changeTitleOnHover: false,
            changePriceOnHover: false,
            changeCompareAtPriceOnHover: false,
            changeUrlOnHover: true,
            defaultUrl: null,
            defaultTitle: null,
            defaultPrice: null,
            defaultCompareAtPrice: null,
            changeSettingsOnTouch: false,
        };

        this.dom = {
            productCard: null,
            productCardUrls: [],
            productCardImage: null,
            imageContainer: null,
            titleContainers: null,
            priceContainers: null,
            compareAtPriceContainers: null,
            swatches: [],
            defaultSwatch: null,
        };

        // Bind methods to preserve context
        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
        this.handleClick = this.handleClick.bind(this);
        this.pointerDeviceChange = this.pointerDeviceChange.bind(this);
        this.removeAllCurrentClassesOnDesktop = this.removeAllCurrentClassesOnDesktop.bind(this);
    }

    connectedCallback() {
        this.initializeElements();
        this.initializeSettings();
        this.initializeDefaults();
        this.addEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
        this.cleanup();
    }

    initializeElements() {
        const productCardSelector = this.getAttribute(this.CONSTANTS.SELECTORS.PRODUCT_CARD) || '';
        const productCard = this.closest(productCardSelector);
        const productCardUrls = productCard?.querySelectorAll('a:not(.gtc-swatch)');
        const productCardImage = productCard?.querySelector(this.getAttribute(this.CONSTANTS.SELECTORS.IMAGE) || '');

        this.dom = {
            productCard,
            productCardUrls: Array.from(productCardUrls),
            productCardImage,
            imageContainer: null,
            swatches: Array.from(this.children).filter(child => child.matches(this.CONSTANTS.SELECTORS.SWATCH)),
            titleContainers: this.getContainerElements(this.CONSTANTS.SELECTORS.TITLE, productCard),
            priceContainers: this.getContainerElements(this.CONSTANTS.SELECTORS.PRICE, productCard),
            compareAtPriceContainers: this.getContainerElements(this.CONSTANTS.SELECTORS.COMPARE_AT_PRICE, productCard),
            defaultSwatch: Array.from(this.children).find(child => child.classList.contains(this.CONSTANTS.CLASSES.CURRENT)),
        };
    }

    getContainerElements(selector, productCard) {
        const selectorValue = this.getAttribute(selector);
        return selectorValue ? productCard?.querySelectorAll(selectorValue) : null;
    }

    initializeSettings() {
        this.settings.changeImageOnHover = this.hasAttribute(this.CONSTANTS.SELECTORS.IMAGE);
        this.settings.changeTitleOnHover = this.hasAttribute(this.CONSTANTS.SELECTORS.TITLE);
        this.settings.changePriceOnHover = this.hasAttribute(this.CONSTANTS.SELECTORS.PRICE);
        this.settings.changeCompareAtPriceOnHover = this.hasAttribute(this.CONSTANTS.SELECTORS.COMPARE_AT_PRICE);
        this.settings.changeSettingsOnTouch = this.hasAttribute(this.CONSTANTS.ATTRIBUTES.CHANGE_SETTINGS_ON_TOUCH);
    }

    initializeDefaults() {
        if (this.settings.changeUrlOnHover && this.dom.productCardUrls?.length > 0) {
            this.settings.defaultUrl = this.dom.productCardUrls[0]?.getAttribute('href');
        }

        if (this.settings.changeTitleOnHover && this.dom.titleContainers?.length > 0) {
            this.settings.defaultTitle = this.dom.titleContainers[0]?.textContent;
        }

        if (this.settings.changePriceOnHover && this.dom.priceContainers?.length > 0) {
            this.settings.defaultPrice = this.dom.priceContainers[0]?.textContent;
        }

        if (this.settings.changeCompareAtPriceOnHover && this.dom.compareAtPriceContainers?.length > 0) {
            this.settings.defaultCompareAtPrice = this.dom.compareAtPriceContainers[0]?.textContent;
        }
    }

    addEventListeners() {

        this.dom.swatches.forEach(swatch => {
            swatch.addEventListener('mouseenter', this.handleMouseEnter);
            swatch.addEventListener('mouseleave', this.handleMouseLeave);
            POINTER_QUERY.addEventListener('change', this.pointerDeviceChange);
            if (this.settings.changeSettingsOnTouch === true) {
                swatch.addEventListener('click', this.handleClick);
            }
        });
    }

    removeEventListeners() {
        this.dom.swatches.forEach(swatch => {
            swatch.removeEventListener('mouseenter', this.handleMouseEnter);
            swatch.removeEventListener('mouseleave', this.handleMouseLeave);
            POINTER_QUERY.removeEventListener('change', this.pointerDeviceChange);
            if (this.settings.changeSettingsOnTouch === true) {
                swatch.removeEventListener('click', this.handleClick);
            }
        });
    }

    handleMouseEnter(event) {
        if (this.IS_POINTER_DEVICE) {
            this.setActiveSwatch(event);
        }
    }

    handleMouseLeave(event) {
        if (this.IS_POINTER_DEVICE) {
            this.setDefaultSwatch(event);
        }
    }

    handleClick(event) {
        if (!this.IS_POINTER_DEVICE) {
            event.preventDefault();
            this.setActiveSwatch(event);

            const clickedSwatch = event.currentTarget;
            this.dom.swatches.forEach(swatch => {
                if (swatch !== clickedSwatch) {
                    swatch.classList.remove(this.CONSTANTS.CLASSES.JS_CURRENT);
                } else {
                    swatch.classList.add(this.CONSTANTS.CLASSES.JS_CURRENT);
                }

            });
            if (this.settings.changeImageOnHover && this.dom.imageContainer) {
                if (clickedSwatch === this.dom.defaultSwatch) {
                    // alert('The code below does not work af intended')
                    this.hideImageContainer(true);
                } else {
                    this.hideImageContainer(false);
                }
            }
        }
    }

    setActiveSwatch(event) {
        const swatch = event.currentTarget;
        if (swatch.classList.contains(this.CONSTANTS.CLASSES.BUTTON)) return;

        if (this.settings.changeImageOnHover) {
            this.createImageContainer();
            this.setImage(swatch);
        }

        this.updateContentOnHover(swatch, true);
    }

    setDefaultSwatch(event) {
        const swatch = event.currentTarget;

        if (this.settings.changeImageOnHover) {
            this.removeAllActiveClasses();
        }

        this.updateContentOnHover(swatch, false);
    }


    pointerDeviceChange() {
        this.IS_POINTER_DEVICE = POINTER_QUERY.matches;
        this.removeAllCurrentClassesOnDesktop();
        this.restoreDefaults();
    }

    removeAllCurrentClassesOnDesktop() {
        this.dom.swatches.forEach(swatch => {
            swatch.classList.remove(this.CONSTANTS.CLASSES.JS_CURRENT);
        });

    }


    updateContentOnHover(swatch, isEntering) {
        if (isEntering) {
            if (this.settings.changeUrlOnHover && this.dom.productCardUrls && this.settings.defaultUrl !== null) {
                this.updateUrl(swatch.getAttribute('data-url'));
            }
            if (this.settings.changeTitleOnHover && this.dom.titleContainers && this.settings.defaultTitle !== null) {
                this.updateTitle(swatch.getAttribute('data-title'));
            }
            if (this.settings.changePriceOnHover && this.dom.priceContainers && this.settings.defaultPrice !== null) {

                this.updatePrice(swatch.getAttribute('data-price'));
            }
            if (this.settings.changeCompareAtPriceOnHover && this.dom.compareAtPriceContainers && this.settings.defaultCompareAtPrice !== null) {
                this.updateCompareAtPrice(swatch.getAttribute('data-compare-at-price'));
            }
            this.updateCompareAtPrice(swatch.getAttribute('data-compare-at-price'));
        } else {
            this.restoreDefaults();
        }
    }

    updateUrl(url) {
        if (!this.settings.changeUrlOnHover || !this.dom.productCardUrls || this.settings.defaultUrl === null) return;
        for (const urlContainer of this.dom.productCardUrls) {
            urlContainer.setAttribute('href', url);
        }
    }

    updateTitle(title) {
        if (!this.settings.changeTitleOnHover || !this.dom.titleContainers || this.settings.defaultTitle === null) return;
        for (const titleContainer of this.dom.titleContainers) {
            titleContainer.textContent = title;
        }
    }

    updatePrice(price) {
        if (!this.settings.changePriceOnHover || !this.dom.priceContainers || this.settings.defaultPrice === null) return;

        for (const priceContainer of this.dom.priceContainers) {
            priceContainer.textContent = price;
        }
    }

    updateCompareAtPrice(compareAtPrice) {
        if (!this.settings.changeCompareAtPriceOnHover || !this.dom.compareAtPriceContainers || this.settings.defaultCompareAtPrice === null) return;
        for (const priceContainer of this.dom.compareAtPriceContainers) {
            priceContainer.textContent = compareAtPrice;
        }
    }

    restoreDefaults() {
        if (this.settings.changeUrlOnHover && this.dom.productCardUrls && this.settings.defaultUrl !== null) {
            for (const urlContainer of this.dom.productCardUrls) {
                urlContainer.setAttribute('href', this.settings.defaultUrl);
            }
        }

        if (this.settings.changeImageOnHover && this.dom.imageContainer) {
            this.removeImageContainer();
        }

        if (this.settings.changeTitleOnHover && this.dom.titleContainers && this.settings.defaultTitle !== null) {
            for (const titleContainer of this.dom.titleContainers) {
                titleContainer.textContent = this.settings.defaultTitle;
            }
        }

        if (this.settings.changePriceOnHover && this.dom.priceContainers && this.settings.defaultPrice !== null) {
            for (const priceContainer of this.dom.priceContainers) {
                priceContainer.textContent = this.settings.defaultPrice;
            }
        }

        if (this.settings.changeCompareAtPriceOnHover && this.dom.compareAtPriceContainers && this.settings.defaultCompareAtPrice !== null) {
            for (const priceContainer of this.dom.compareAtPriceContainers) {
                priceContainer.textContent = this.settings.defaultCompareAtPrice;
            }
        }
    }

    createImageContainer() {
        if (this.dom.imageContainer) return;

        const imageContainer = document.createElement('div');
        imageContainer.classList.add(this.CONSTANTS.CLASSES.IMAGE_CONTAINER);
        this.dom.imageContainer = imageContainer;
        this.dom.productCardImage.appendChild(imageContainer);
    }

    removeImageContainer() {
        if (!this.dom.imageContainer) return;
        this.dom.productCardImage.removeChild(this.dom.imageContainer);
        this.dom.imageContainer = null;
    }

    hideImageContainer(hide = true) {
        if (!this.dom.imageContainer) return;
        if (hide) {
            this.dom.imageContainer.classList.add(this.CONSTANTS.CLASSES.HIDE);
        } else {
            this.dom.imageContainer.classList.remove(this.CONSTANTS.CLASSES.HIDE);
        }
    }
    getImageData(swatch) {
        try {
            const imageData = swatch.getAttribute('data-image');
            return imageData ? JSON.parse(imageData) : null;
        } catch (error) {
            console.error('Error parsing image data:', error);
            return null;
        }
    }

    setImage(swatch) {
        if (!this.dom.imageContainer) return;

        const imageData = this.getImageData(swatch);
        if (!imageData) return;

        const imageSrc = this.extractImageSrc(imageData);
        if (!imageSrc) return;

        const existingImage = this.findExistingImage(imageSrc);

        if (existingImage) {
            this.activateImage(existingImage);
        } else {
            this.createAndLoadImage(imageData);
        }
    }

    extractImageSrc(imageData) {
        // Use DOMParser for better performance than creating temporary div
        const parser = new DOMParser();
        const doc = parser.parseFromString(imageData, 'text/html');
        const imgElement = doc.querySelector('img');
        return imgElement?.src || null;
    }

    findExistingImage(imageSrc) {
        if (!this.dom.imageContainer) return null;

        const images = this.dom.imageContainer.querySelectorAll(this.CONSTANTS.SELECTORS.IMAGE_ITEM);
        return Array.from(images).find(image => {
            const imgElement = image.querySelector('img');
            return imgElement?.src === imageSrc;
        }) || null;
    }

    activateImage(imageElement) {
        if (!this.dom.imageContainer) return;
        this.removeAllActiveClasses();
        imageElement.classList.add(this.CONSTANTS.CLASSES.ACTIVE);
        this.dom.imageContainer.classList.remove(this.CONSTANTS.CLASSES.SPINNER);
    }

    createAndLoadImage(imageData) {
        this.removeAllActiveClasses();

        const imageElement = document.createElement('div');
        imageElement.innerHTML = imageData;
        imageElement.classList.add(this.CONSTANTS.CLASSES.IMAGE_ITEM);

        this.dom.imageContainer.appendChild(imageElement);

        const imgElement = imageElement.querySelector('img');
        if (imgElement) {
            this.handleImageLoad(imgElement, imageElement);
        }
    }

    handleImageLoad(imgElement, imageElement) {
        if (imgElement.complete) {
            this.activateImage(imageElement);
        } else {
            let spinnerTimeout = setTimeout(() => {
                if (this.dom.imageContainer) {
                    this.dom.imageContainer.classList.add(this.CONSTANTS.CLASSES.SPINNER);
                }
            }, this.CONSTANTS.SPINNER_DELAY);

            imgElement.onload = () => {
                clearTimeout(spinnerTimeout);
                this.activateImage(imageElement);
            };

            imgElement.onerror = () => {
                clearTimeout(spinnerTimeout);
                console.error('Failed to load image:', imgElement.src);
                if (this.dom.imageContainer) {
                    this.dom.imageContainer.classList.remove(this.CONSTANTS.CLASSES.SPINNER);
                }
            };
        }
    }

    removeAllActiveClasses() {
        if (!this.dom.imageContainer) return;

        const images = this.dom.imageContainer.querySelectorAll(this.CONSTANTS.SELECTORS.IMAGE_ITEM);
        images.forEach(image => image.classList.remove(this.CONSTANTS.CLASSES.ACTIVE));
    }

    cleanup() {
        this.dom = {
            productCard: null,
            productCardImage: null,
            imageContainer: null,
            titleContainer: null,
            priceContainer: null,
            compareAtPriceContainer: null,
            swatches: [],
        };
    }
}

if (!customElements.get('gtc-product-card-swatches')) {
    customElements.define('gtc-product-card-swatches', GtcProductCardSwatches);
}


/**
 * GtcProductPageSwatches
 *
 * @description
 * Handles the product page swatches functionality, including hover interactions
 * for changing titles on product page.
 */


class GtcProductPageSwatches extends HTMLElement {
    constructor() {

        super();

        this.CONSTANTS = {
            SELECTORS: {
                TITLE: '[data-current-title]',
                SWATCHES_CONTAINER: '.gtc-swatches',
                SWATCH: '.gtc-swatch',
            },
            ATTRIBUTES: {
                TITLE: 'data-current-title',
                COLOR_NAME: 'data-color-name',
            },
        };

        this.settings = {
            changeTitleOnHover: false,
            defaultTitle: null,
        };

        this.dom = {
            titleContainer: this.querySelector(this.CONSTANTS.SELECTORS.TITLE),
            swatches: Array.from(
                this.querySelector(this.CONSTANTS.SELECTORS.SWATCHES_CONTAINER)
                    .children
            ).filter(child => child.matches(this.CONSTANTS.SELECTORS.SWATCH)),
        };

        this.handleMouseEnter = this.handleMouseEnter.bind(this);
        this.handleMouseLeave = this.handleMouseLeave.bind(this);
    }

    connectedCallback() {
        this.initializeSettings();
        this.initializeDefaults();
        this.addEventListeners();
    }

    disconnectedCallback() {
        this.removeEventListeners();
        this.cleanup();
    }

    initializeSettings() {
        this.settings.changeTitleOnHover = this.querySelector(this.CONSTANTS.SELECTORS.TITLE) !== null;
    }

    initializeDefaults() {
        if (this.settings.changeTitleOnHover && this.dom.titleContainer && this.settings.defaultTitle === null) {
            this.settings.defaultTitle = this.dom.titleContainer.getAttribute(this.CONSTANTS.ATTRIBUTES.TITLE);
        }
    }

    addEventListeners() {
        this.dom.swatches.forEach(swatch => {
            swatch.addEventListener('mouseenter', this.handleMouseEnter);
            swatch.addEventListener('mouseleave', this.handleMouseLeave);
        });
    }

    removeEventListeners() {
        this.dom.swatches.forEach(swatch => {
            swatch.removeEventListener('mouseenter', this.handleMouseEnter);
            swatch.removeEventListener('mouseleave', this.handleMouseLeave);
        });
    }

    handleMouseEnter(event) {
        this.setActiveSwatch(event);
    }

    handleMouseLeave(event) {
        this.restoreDefaults();
    }

    setActiveSwatch(event) {
        const swatch = event.currentTarget;

        if (this.settings.changeTitleOnHover && this.dom.titleContainer && this.settings.defaultTitle !== null) {
            const title = swatch.getAttribute(this.CONSTANTS.ATTRIBUTES.COLOR_NAME);
            if (title) {
                this.updateTitle(title);
            }
        }
    }

    updateTitle(title) {
        if (!this.settings.changeTitleOnHover || !this.dom.titleContainer || this.settings.defaultTitle === null) return;
        this.dom.titleContainer.textContent = title;
    }

    restoreDefaults() {
        if (this.settings.changeTitleOnHover && this.dom.titleContainer && this.settings.defaultTitle !== null) {
            this.dom.titleContainer.textContent = this.settings.defaultTitle;
        }
    }

    showAllSwatches() {
        this.querySelector('.gtc-swatches--hidden').classList.remove('hidden');
    }
}

if (!customElements.get('gtc-product-page-swatches')) {
    customElements.define('gtc-product-page-swatches', GtcProductPageSwatches);
}


/**
 * GtcOpenerSwatches
 *
 * @description
 * Handles the opener swatches functionality, including hover interactions
 * for changing titles on opener.
 */

class GtcOpenerSwatches extends GtcProductPageSwatches {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
    }

    disconnectedCallback() {
        super.disconnectedCallback();
    }
}

if (!customElements.get('gtc-opener-swatches')) {
    customElements.define('gtc-opener-swatches', GtcOpenerSwatches);
}